import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2, MessageCircle, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useFlow } from "../context/FlowContext";
import { API, track } from "../api";
import { Modal, field, label } from "./Modal";
import { PremiumIcon } from "./PremiumIcon";
import { Consent } from "./Consent";

const ORDER = ["hogar", "mascotas", "acompanamiento", "recados"];

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-all duration-300 ${active ? "bg-forest text-white border-forest" : "bg-white text-ink-soft border-line hover:border-forest"}`} data-testid={testid}>
    {children}
  </button>
);

export const RequestWizard = () => {
  const { t, lang } = useLang();
  const w = t.wizard;
  const { client, closeClient } = useFlow();
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [ref, setRef] = useState("");
  const [wa, setWa] = useState("");
  const [form, setForm] = useState({
    category: "", postal_code: "", neighborhood: "", address_hint: "",
    requested_date: "", custom_date: "", time_range: "", duration: "",
    description: "", name: "", phone: "", email: "", preferred_contact: "",
  });
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const [ai, setAi] = useState({ loading: false, category: null, summary: "" });
  const [consent, setConsent] = useState(false);
  const analyze = async () => {
    if (!form.description.trim()) return;
    setAi((a) => ({ ...a, loading: true }));
    try {
      const { data } = await axios.post(`${API}/ai/classify`, { text: form.description, lang });
      setAi({ loading: false, category: data.category, summary: data.summary });
    } catch (e) {
      setAi({ loading: false, category: null, summary: "" });
      setError(t.ai.error);
    }
  };

  useEffect(() => {
    if (client.open) {
      setStep(0); setStatus("idle"); setError(""); setRef("");
      setConsent(false);
      setAi({ loading: false, category: null, summary: "" });
      setForm({ category: client.category || "", postal_code: "", neighborhood: "", address_hint: "", requested_date: "", custom_date: "", time_range: "", duration: "", description: "", name: "", phone: "", email: "", preferred_contact: "" });
    }
  }, [client.open, client.category]);

  const next = () => {
    setError("");
    if (step === 0 && !form.category) return setError(w.required);
    if (step === 1 && !form.postal_code.trim()) return setError(w.required);
    if (step === 2 && !form.requested_date) return setError(w.required);
    setStep((s) => Math.min(s + 1, 4));
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) return setError(w.required);
    if (!consent) return setError(t.common.privacyErr);
    setStatus("loading");
    const requested_date = form.requested_date === w.step3.when[3] && form.custom_date ? form.custom_date : form.requested_date;
    try {
      const { data } = await axios.post(`${API}/requests`, {
        category: form.category, postal_code: form.postal_code, neighborhood: form.neighborhood,
        address_hint: form.address_hint, requested_date, time_range: form.time_range, duration: form.duration,
        description: form.description, name: form.name, phone: form.phone, email: form.email,
        preferred_contact: form.preferred_contact, ai_summary: ai.summary, consent: true, lang,
      });
      setRef(data.ref);
      try { const cfg = await axios.get(`${API}/config`); setWa(cfg.data.whatsapp_number || ""); } catch (e) {}
      track("request_created", form.category);
      setStatus("success");
    } catch (e) {
      setStatus("idle"); setError(w.error);
    }
  };

  const waMsg = lang === "en"
    ? `Hi, I just made a request on HomeHealthy and need help with my service. My request is #${ref}.`
    : `Hola, acabo de realizar una solicitud en HomeHealthy y necesito ayuda con mi servicio. Mi solicitud es #${ref}.`;
  const waLink = wa ? `https://wa.me/${wa.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(waMsg)}` : null;

  return (
    <Modal open={client.open} onClose={closeClient} testid="request-wizard">
      {status === "success" ? (
        <div className="p-8 md:p-12 text-center" data-testid="request-success">
          <div className="w-16 h-16 rounded-full bg-forest text-white flex items-center justify-center mx-auto"><Check size={30} strokeWidth={3} /></div>
          <h3 className="mt-5 font-heading text-2xl md:text-3xl font-medium text-ink">{w.success.title}</h3>
          <p className="mt-3 text-ink-soft leading-relaxed max-w-md mx-auto">{w.success.body}</p>
          <div className="mt-6 inline-flex flex-col items-center rounded-2xl bg-cream border border-line px-8 py-4">
            <span className="text-xs uppercase tracking-wider text-ink-faint">{w.success.refLabel}</span>
            <span className="font-heading text-2xl font-bold text-forest" data-testid="request-ref">{ref}</span>
          </div>
          <div className="mt-8 rounded-2xl border border-line bg-cream/60 p-5 text-left" data-testid="request-support">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-petrol/10 text-petrol flex items-center justify-center shrink-0"><MessageCircle size={20} /></span>
              <div className="flex-1">
                <div className="font-heading font-medium text-ink">{w.success.supportTitle}</div>
                <div className="text-sm text-ink-faint">{w.success.supportBody}</div>
              </div>
            </div>
            {waLink ? (
              <a href={waLink} target="_blank" rel="noreferrer" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-petrol text-white font-semibold px-6 py-3 hover:opacity-90 transition-all" data-testid="request-whatsapp">
                <MessageCircle size={18} /> {w.success.whatsapp}
              </a>
            ) : (
              <button disabled title="Disponible pronto" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-ink-faint font-semibold px-6 py-3 border border-line cursor-not-allowed" data-testid="request-whatsapp-preview">
                <MessageCircle size={18} /> {w.success.whatsapp}
              </button>
            )}
          </div>
          <button onClick={closeClient} className="mt-4 inline-flex items-center justify-center rounded-full border border-line text-ink font-semibold px-6 py-3.5 hover:bg-cream transition-all" data-testid="request-back">
            {w.success.back}
          </button>
        </div>
      ) : (
        <div className="p-6 md:p-10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">{w.title}</span>
          <div className="mt-4 flex gap-1.5">
            {w.steps.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-forest" : "bg-line"}`} />
            ))}
          </div>
          <h3 className="mt-5 font-heading text-2xl md:text-3xl font-medium text-ink">{w.steps[step]}</h3>

          <div className="mt-6 min-h-[220px]">
            {step === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wizard-step-0">
                {ORDER.map((key) => (
                  <button key={key} type="button" onClick={() => upd("category", key)} className={`text-left rounded-2xl border p-5 transition-all duration-300 ${form.category === key ? "border-forest bg-cream ring-2 ring-forest/20" : "border-line hover:border-forest"}`} data-testid={`wizard-cat-${key}`}>
                    <PremiumIcon category={key} active={form.category === key} hoverGroup={false} size={22} className="!w-10 !h-10" />
                    <div className="mt-2 font-heading text-lg font-medium text-ink">{t.categories[key].label}</div>
                    <div className="text-sm text-ink-faint">{t.categories[key].desc}</div>
                  </button>
                ))}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4" data-testid="wizard-step-1">
                <div><label className={label}>{w.step2.postal} *</label><input className={field} value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} data-testid="wizard-postal" /></div>
                <div><label className={label}>{w.step2.neighborhood}</label><input className={field} value={form.neighborhood} onChange={(e) => upd("neighborhood", e.target.value)} data-testid="wizard-neighborhood" /></div>
                <div><label className={label}>{w.step2.address}</label><input className={field} value={form.address_hint} onChange={(e) => upd("address_hint", e.target.value)} data-testid="wizard-address" /></div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5" data-testid="wizard-step-2">
                <div>
                  <div className="flex flex-wrap gap-2">
                    {w.step3.when.map((opt) => <Chip key={opt} active={form.requested_date === opt} onClick={() => upd("requested_date", opt)} testid={`wizard-when-${opt}`}>{opt}</Chip>)}
                  </div>
                  {form.requested_date === w.step3.when[3] && (
                    <input type="date" className={field + " mt-3"} value={form.custom_date} onChange={(e) => upd("custom_date", e.target.value)} data-testid="wizard-date" />
                  )}
                </div>
                <div>
                  <label className={label}>{w.step3.timeRange}</label>
                  <div className="flex flex-wrap gap-2">
                    {w.step3.timeOptions.map((opt) => <Chip key={opt} active={form.time_range === opt} onClick={() => upd("time_range", opt)}>{opt}</Chip>)}
                  </div>
                </div>
                <div>
                  <label className={label}>{w.step3.duration}</label>
                  <select className={field} value={form.duration} onChange={(e) => upd("duration", e.target.value)} data-testid="wizard-duration">
                    <option value="">—</option>
                    {w.step3.durationOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="wizard-step-3">
                <label className={label}>{w.step4.title}</label>
                <textarea rows={5} className={field} placeholder={w.step4.placeholder} value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="wizard-description" />
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button type="button" onClick={analyze} disabled={ai.loading || !form.description.trim()} className="inline-flex items-center gap-2 rounded-full border border-forest text-forest font-semibold px-4 py-2 text-sm hover:bg-forest hover:text-white transition-all disabled:opacity-40" data-testid="wizard-ai-analyze">
                    {ai.loading ? <><Loader2 size={15} className="animate-spin" /> {t.ai.analyzing}</> : <><Sparkles size={15} /> {t.ai.analyze}</>}
                  </button>
                  {ai.category && ai.category !== "otro" && (
                    <div className="inline-flex items-center gap-2 text-sm" data-testid="wizard-ai-suggestion">
                      <span className="text-ink-faint">{t.ai.suggested}:</span>
                      <span className="font-semibold text-ink">{t.categories[ai.category]?.label || ai.category}</span>
                      {form.category !== ai.category && (
                        <button type="button" onClick={() => upd("category", ai.category)} className="rounded-full bg-cream-deep px-3 py-1 text-xs font-semibold text-forest hover:bg-sage-light" data-testid="wizard-ai-apply">{t.ai.apply}</button>
                      )}
                    </div>
                  )}
                </div>
                {ai.summary && <p className="mt-2 text-sm text-ink-faint italic">{ai.summary}</p>}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4" data-testid="wizard-step-4">
                <div><label className={label}>{w.step5.name} *</label><input className={field} value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="wizard-name" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={label}>{w.step5.phone} *</label><input className={field} value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="wizard-phone" /></div>
                  <div><label className={label}>{w.step5.email} *</label><input type="email" className={field} value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="wizard-email" /></div>
                </div>
                <div>
                  <label className={label}>{w.step5.contact}</label>
                  <div className="flex flex-wrap gap-2">
                    {w.step5.contactOptions.map((opt) => <Chip key={opt} active={form.preferred_contact === opt} onClick={() => upd("preferred_contact", opt)}>{opt}</Chip>)}
                  </div>
                </div>
                <Consent checked={consent} onChange={setConsent} testid="wizard-consent" />
              </div>
            )}
          </div>

          {error && <p className="mt-3 text-sm text-red-600" data-testid="wizard-error">{error}</p>}

          <div className="mt-6 flex items-center justify-between gap-3">
            <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-2 rounded-full border border-line text-ink-soft font-semibold px-5 py-3 disabled:opacity-0 hover:bg-cream transition-all" data-testid="wizard-back">
              <ArrowLeft size={16} /> {w.back}
            </button>
            {step < 4 ? (
              <button onClick={next} className="group inline-flex items-center gap-2 rounded-full bg-forest text-white font-semibold px-7 py-3.5 hover:bg-sage-deep transition-all hover:-translate-y-0.5" data-testid="wizard-next">
                {w.next} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            ) : (
              <button onClick={submit} disabled={status === "loading"} className="inline-flex items-center gap-2 rounded-full bg-forest text-white font-semibold px-7 py-3.5 hover:bg-sage-deep transition-all disabled:opacity-60" data-testid="wizard-submit">
                {status === "loading" ? <><Loader2 size={16} className="animate-spin" /> {w.submitting}</> : w.submit}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};
